import React, { useState, useEffect } from 'react';
import {
    Modal,
    Button,
    Box,
    Text,
    Loader,
    ActionIcon,
    Group,
    Tooltip
} from '@mantine/core';
import {
    IconX,
    IconDeviceFloppy,
    IconEdit,
    IconEye
} from '@tabler/icons-react';
import { MODAL_SIZES } from '../../theme/mantineTheme';
import { entityService } from '../../services/entityServiceSingleton';
import SetForm, { SetFormData } from '../common/SetForm';
import { debugLog } from '../../utils/debugLog';
import { DialogErrorAlert } from './DialogErrorAlert';

interface SetPreviewDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: (createdSet: any) => void;
    initialData?: SetFormData;
    mode?: 'preview' | 'edit';
    title?: string;
}

const SetPreviewDialog: React.FC<SetPreviewDialogProps> = ({
    open,
    onClose,
    onSuccess,
    initialData,
    mode = 'preview',
    title
}) => {
    const [formData, setFormData] = useState<SetFormData>(initialData || {
        displayName: '',
        description: '',
        filterScope: '',
        type: 'manual',
        isSystem: false,
        manualMembers: [],
        filter: ''
    });
    // If mode is explicitly 'edit', use edit mode. Otherwise, if initialData is provided (for generated sets), start in edit mode
    const [isEditMode, setIsEditMode] = useState(mode === 'edit' || (mode === 'preview' && !!initialData));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [cancelToken, setCancelToken] = useState<AbortController | null>(null);

    // Update form data when initialData changes
    useEffect(() => {
        if (initialData) {
            setFormData(initialData);
            // If mode is preview and we have initialData, start in edit mode so user can create directly
            if (mode === 'preview') {
                setIsEditMode(true);
            }
        } else {
            // Reset to preview mode if no initialData
            if (mode === 'preview') {
                setIsEditMode(false);
            }
        }
    }, [initialData, mode]);

    const handleFormChange = (data: SetFormData) => {
        setFormData(data);
    };

    const handleEdit = () => {
        setIsEditMode(true);
    };

    const handleSave = async () => {
        // Cancel any existing request
        if (cancelToken) {
            cancelToken.abort();
        }

        // Create new abort controller
        const newCancelToken = new AbortController();
        setCancelToken(newCancelToken);

        setLoading(true);
        setError(null);

        try {
            // Create the Set entity structure
            const setEntity: any = {
                displayName: formData.displayName
            };

            // Add optional fields only if they have values
            if (formData.description && formData.description.trim()) {
                setEntity.description = formData.description;
            }

            if (formData.filterScope && formData.filterScope.trim()) {
                setEntity.filterScope = formData.filterScope;
            }

            setEntity.type = formData.type;
            setEntity.isSystem = formData.isSystem;

            // Add mode-specific properties
            if (formData.type === 'manual' && formData.manualMembers.length > 0) {
                setEntity.ManualSetMember = formData.manualMembers
                    .map((m: any) => typeof m === 'string' ? m : (m?.Id ?? m?.id ?? ''))
                    .map((v: any) => (v ?? '').toString().trim())
                    .filter((v: string) => v.length > 0);
            } else if (formData.type === 'enumerated' && formData.filter && formData.filter.trim()) {
                setEntity.filter = formData.filter;
            }

            // Create the Set via API
            const createdSet = await entityService.createEntity('Set', setEntity);

            if (onSuccess) {
                onSuccess(createdSet);
            }

            onClose();

        } catch (err: any) {
            // Check if request was cancelled
            if (err.name === 'AbortError' || newCancelToken.signal.aborted) {
                debugLog('Set creation was cancelled');
                setCancelToken(null);
                setLoading(false);
                return;
            }

            console.error('Error creating Set:', err);
            setError(err instanceof Error ? err.message : 'Failed to create Set');
        } finally {
            setLoading(false);
            setCancelToken(null);
        }
    };

    const handleCancelOperation = () => {
        if (cancelToken) {
            cancelToken.abort();
            setCancelToken(null);
            setLoading(false);
            setError('Operation cancelled');
        }
    };

    const handleClose = () => {
        // Cancel any pending request
        if (cancelToken) {
            cancelToken.abort();
            setCancelToken(null);
        }
        onClose();
    };

    const isFormValid = () => {
        return formData.displayName.trim() !== '' &&
            formData.filterScope.trim() !== '' &&
            (formData.type === 'enumerated' || formData.manualMembers.length > 0);
    };

    return (
        <Modal
            opened={open}
            onClose={handleClose}
            size={MODAL_SIZES.detail}
            title={
                <Tooltip label="SetPreviewDialog" withArrow position="top">
                    <span style={{ cursor: 'help' }}>
                        <Group justify="space-between" flex={1} w="100%" component="span">
                            <Group gap="xs" component="span">
                                {isEditMode ? <IconEdit size={20} /> : <IconEye size={20} />}
                                <Text fw={600} component="span">
                                    {title || (isEditMode ? 'Edit Set' : 'Preview Set')}
                                </Text>
                            </Group>
                            <ActionIcon
                        variant="subtle"
                        onClick={handleClose}
                        disabled={loading}
                        aria-label="Close"
                    >
                        <IconX size={18} />
                    </ActionIcon>
                        </Group>
                    </span>
                </Tooltip>
            }
            withCloseButton={false}
            closeOnClickOutside={!loading}
            closeOnEscape={!loading}
        >
            <DialogErrorAlert error={error} onClose={() => setError(null)} />

            <SetForm
                data={formData}
                onChange={handleFormChange}
                disabled={loading}
                showSystemToggle={true}
                showDescription={true}
                showPreviewMembers={true}
            />

            <Group justify="flex-end" mt="lg">
                {loading ? (
                    <Button
                        onClick={handleCancelOperation}
                        color="red"
                        variant="outline"
                        leftSection={<IconX size={16} />}
                    >
                        Cancel
                    </Button>
                ) : (
                    <>
                        {!isEditMode && (
                            <Button
                                onClick={handleEdit}
                                variant="outline"
                                leftSection={<IconEdit size={16} />}
                            >
                                Edit
                            </Button>
                        )}
                        <Button onClick={handleClose}>
                            {isEditMode ? 'Cancel' : 'Close'}
                        </Button>
                        {isEditMode && (
                            <Button
                                onClick={handleSave}
                                variant="filled"
                                leftSection={loading ? <Loader size={16} color="white" /> : <IconDeviceFloppy size={16} />}
                                disabled={!isFormValid() || loading}
                            >
                                {loading ? 'Creating...' : 'Create Set'}
                            </Button>
                        )}
                    </>
                )}
            </Group>
        </Modal>
    );
};

export default SetPreviewDialog;
